import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { STAGE_LABELS, currency, timeAgo } from "@/lib/constants";
import SLABadge from "@/components/SLABadge";
import TrackerTable from "@/components/TrackerTable";
import { FileText, Filter, Search, CheckCircle2, Clock, Send, ExternalLink } from "lucide-react";

const QUICK_FILTERS = [
  { id: "all", label: "All", icon: FileText },
  { id: "active", label: "In Progress", icon: Clock },
  { id: "executed", label: "Executed", icon: CheckCircle2 },
  { id: "unexecuted", label: "Unexecuted", icon: Send },
];

export default function MyContracts() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("search", q);
      if (filter === "executed") params.set("executed_only", "true");
      else if (filter === "unexecuted") params.set("executed_only", "false");
      const { data } = await api.get(`/contracts/scope/mine?${params.toString()}`);
      setRows(data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const filtered = useMemo(() => {
    if (filter === "active") return rows.filter(c => c.stage !== "executed");
    return rows;
  }, [rows, filter]);

  const stats = useMemo(() => ({
    total: rows.length,
    executed: rows.filter(c => c.stage === "executed").length,
    active: rows.filter(c => c.stage !== "executed").length,
    overdue: rows.filter(c => c.sla_status === "overdue").length,
  }), [rows]);

  return (
    <div className="p-6" data-testid="my-contracts-page">
      <div className="mb-6">
        <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Phase 4 · Requester View</div>
        <h1 className="text-2xl font-bold text-slate-900">My Contracts</h1>
        <p className="text-sm text-slate-500 mt-1">
          Contracts you've submitted (or that were submitted on your behalf). Restricted documents and internal legal notes are hidden.
        </p>
      </div>

      <div className="grid grid-cols-4 gap-3 mb-4">
        <StatTile label="Total" value={stats.total}/>
        <StatTile label="In Progress" value={stats.active}/>
        <StatTile label="Executed" value={stats.executed}/>
        <StatTile label="Overdue" value={stats.overdue} tone={stats.overdue > 0 ? "danger" : "neutral"}/>
      </div>

      <div className="flex flex-wrap gap-2 items-center mb-4">
        {QUICK_FILTERS.map(f => (
          <button key={f.id} onClick={() => setFilter(f.id)} data-testid={`mc-filter-${f.id}`}
                  className={`h-9 px-3 text-xs font-semibold rounded-md border flex items-center gap-1.5 ${
                    filter === f.id ? "bg-[#005289] text-white border-[#005289]" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"
                  }`}>
            <f.icon className="w-3.5 h-3.5"/> {f.label}
          </button>
        ))}
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="relative ml-auto">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
          <input data-testid="mc-search" value={q} onChange={e => setQ(e.target.value)}
                 placeholder="Search vendor / #contract / description..."
                 className="h-9 pl-9 pr-3 border border-slate-200 rounded-md text-sm w-80"/>
        </form>
      </div>

      <TrackerTable rows={filtered} onChanged={load} testIdPrefix="mc-tt"
                    emptyText={loading ? "Loading…" : "No contracts."} />

      <div className="mt-4 text-xs text-slate-500 flex items-center gap-2">
        <ExternalLink className="w-3 h-3"/>
        Click any row for the read-only Contract Chart view. Internal legal notes and Restricted documents are automatically hidden.
      </div>
    </div>
  );
}

function StatTile({ label, value, tone = "neutral" }) {
  const cls = tone === "danger" ? "text-red-600" : "text-slate-900";
  return (
    <div className="bg-white border border-slate-200 rounded-md p-3">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${cls}`}>{value}</div>
    </div>
  );
}
